/// <reference path="../types/ambient.d.ts" />

/**
 * `@punica/editor-shell-classic` — the classic IDE shell for the Punica
 * substrate (activity bar, sidebars, content/panel tabs, status bar, layout,
 * keyboard shortcuts, markdown, views).
 *
 * The substrate (`@punica/editor`) is headless: it draws no UI. This package
 * contributes the classic UI through the substrate's `registerShell()` seam —
 * a facade merged onto `punica.shell`, the profile-gated shell modules, and the
 * two built-in ApplicationProfiles. A host calls `registerClassicShell()` after
 * `initialize()` and before `bootstrap()`. With `@punica/editor` marked
 * external in the build, a single `punica` runtime instance is shared.
 */

import { registerShell } from '@punica/editor';

import Shell from './impl/shell/index.js';
import {
  ActivityBarModule,
  AppBarModule,
  StatusbarModule,
  ViewModule,
  ViewsModule,
  KeyboardShortcutsModule,
  LayoutModule,
  ContentTabsModule,
  PanelTabsModule,
  PanelLayoutModule
} from './impl/shell/modules/index.js';
import { ivyxClassicProfile } from './impl/shell/profile/ivyxClassic.js';
import { agentOpsConsoleProfile } from './impl/shell/profile/agentOpsConsole.js';

/**
 * Register the classic IDE shell with the substrate.
 *
 * Call after `punica`'s `initialize()` and before `bootstrap()`:
 *
 * ```ts
 * import { initialize, bootstrap, start } from '@punica/editor';
 * import { registerClassicShell } from '@punica/editor-shell-classic';
 *
 * initialize();
 * registerClassicShell();
 * await bootstrap();
 * await start();
 * ```
 */
export function registerClassicShell(): void {
  registerShell({
    facade: Shell,
    moduleFactories: {
      'shell.statusbar': () => new StatusbarModule(),
      'shell.view': () => new ViewModule(),
      'shell.views': () => new ViewsModule(),
      'shell.keyboardShortcuts': () => new KeyboardShortcutsModule(),
      'shell.contentTabs': () => new ContentTabsModule(),
      'shell.panelTabs': () => new PanelTabsModule(),
      'shell.panelLayout': () => new PanelLayoutModule(),
      'shell.layout': () => new LayoutModule(),
      'shell.activityBar': () => new ActivityBarModule(),
      'shell.appBar': () => new AppBarModule()
    },
    profiles: [ivyxClassicProfile, agentOpsConsoleProfile],
    defaultProfileId: 'ivyx-classic'
  });
}
